<script setup lang="ts">
/**
 * Page « Demandes d'accompagnement » — file / dispatch.
 * Table paginée (mock), filtres, et actions rapides modulées par le rôle.
 */
import { computed, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useToast } from 'vue-toast-notification'
import {
  LifebuoyIcon,
  PlusIcon,
  ChartBarIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  UserPlusIcon,
  HandRaisedIcon,
  BoltIcon,
  PlayIcon,
} from '@heroicons/vue/24/outline'
import { useAccompagnementStore } from '@/v2/accompagnement/store/useAccompagnementStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import {
  canAssign,
  canCreate,
  canSelfTake,
  canTakeCharge,
} from '@/v2/accompagnement/helpers/accompagnementPermissions'
import { ACCOMP_STATUS_META, ACCOMP_STATUS_ORDER, ACCOMP_TYPE_LABEL } from '@/v2/accompagnement/constants/accompagnementConstants'
import { formatDateShort } from '@/v2/accompagnement/helpers/accompagnementFormat'
import { assignDelayMs, formatDuration, isTakeChargeOverdue } from '@/v2/accompagnement/helpers/accompagnementDelays'
import type { AccompRequest, AccompSortField } from '@/v2/accompagnement/api/accompagnementTypes'
import AccompStatusBadge from '@/v2/accompagnement/components/AccompStatusBadge.vue'
import AccompUrgencyBadge from '@/v2/accompagnement/components/AccompUrgencyBadge.vue'
import AccompAssignDialog from '@/v2/accompagnement/components/AccompAssignDialog.vue'

const router = useRouter()
const store = useAccompagnementStore()
const perms = useBoostCorePermissionStore()
const toast = useToast()

const {
  rows,
  total,
  loading,
  error,
  page,
  pageCount,
  from,
  to,
  q,
  statusFilter,
  view,
  availableViews,
  sort,
  order,
  actionLoading,
  currentUserUuid,
} = storeToRefs(store)

const COLUMNS: { field: AccompSortField; label: string }[] = [
  { field: 'reference', label: 'Réf.' },
  { field: 'urgency', label: 'Urgence' },
  { field: 'status', label: 'Statut' },
  { field: 'createdAt', label: 'Créée le' },
]

const showCreate = computed(() => canCreate(perms))

const assignTarget = ref<AccompRequest | null>(null)

function goToDetail(req: AccompRequest) {
  router.push(`/accompagnement/${req.id}`)
}

function onSearch(e: Event) {
  store.setQuery((e.target as HTMLInputElement).value)
}

function openAssign(req: AccompRequest) {
  assignTarget.value = req
}

async function onAssignConfirmed(payload: { technicianUuid: string; note?: string }) {
  if (!assignTarget.value) return
  const res = await store.assign(assignTarget.value.id, payload.technicianUuid, payload.note)
  if (res) {
    toast.success('Demande attribuée.')
    assignTarget.value = null
  } else {
    toast.error(store.actionError ?? "Échec de l'attribution.")
  }
}

async function selfTake(req: AccompRequest) {
  const res = await store.assign(req.id, currentUserUuid.value)
  if (res) toast.success('Demande prise.')
  else toast.error(store.actionError ?? 'Action impossible.')
}

async function takeCharge(req: AccompRequest) {
  const res = await store.takeCharge(req.id)
  if (res) toast.success('Prise en charge enregistrée.')
  else toast.error(store.actionError ?? 'Action impossible.')
}

async function selfAssignAndStart(req: AccompRequest) {
  const res = await store.selfAssignAndStart(req.id)
  if (res) toast.success('Demande prise et démarrée.')
  else toast.error(store.actionError ?? 'Action impossible.')
}

const isDispatcher = computed(() => perms.can('ACCOMP_ADMIN'))

// Si le rôle change (ex. switcher DEV) et que la vue active n'est plus permise,
// on repart sur la vue par défaut du nouveau rôle.
watch(availableViews, (views) => {
  if (!views.some((v) => v.value === view.value)) store.initForRole()
})

onMounted(() => {
  store.initForRole()
})
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <header class="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 class="flex items-center gap-2 text-2xl font-bold">
          <LifebuoyIcon class="h-6 w-6 text-primary" aria-hidden="true" />
          Demandes d'accompagnement
        </h1>
        <p class="mt-1 text-base-content/70">
          Suivez, attribuez et prenez en charge les demandes adressées à la technique.
        </p>
      </div>
      <div class="flex items-center gap-2">
        <RouterLink to="/accompagnement/stats" class="btn btn-ghost gap-1">
          <ChartBarIcon class="h-5 w-5" aria-hidden="true" />
          Statistiques
        </RouterLink>
        <RouterLink v-if="showCreate" to="/accompagnement/nouvelle" class="btn btn-primary gap-1">
          <PlusIcon class="h-5 w-5" aria-hidden="true" />
          Nouvelle demande
        </RouterLink>
      </div>
    </header>

    <!-- Recherche -->
    <div
      class="mb-3 flex items-center gap-3 rounded-xl border border-base-300 bg-base-100 px-4 py-2.5 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20"
    >
      <MagnifyingGlassIcon
        class="h-5 w-5 shrink-0"
        :class="loading ? 'text-primary' : 'text-base-content/40'"
        aria-hidden="true"
      />
      <input
        type="text"
        class="flex-1 bg-transparent text-base text-base-content outline-none placeholder:text-base-content/30"
        placeholder="Rechercher (référence, client, demandeur, technicien)…"
        :value="q"
        aria-label="Rechercher une demande"
        @input="onSearch"
      />
      <ArrowPathIcon v-if="loading" class="h-4 w-4 animate-spin text-primary" aria-hidden="true" />
      <button
        v-if="q"
        type="button"
        class="flex h-6 w-6 items-center justify-center rounded-full text-base-content/40 transition-colors hover:bg-base-200 hover:text-base-content"
        aria-label="Effacer la recherche"
        @click="store.setQuery('')"
      >
        <XMarkIcon class="h-4 w-4" />
      </button>
    </div>

    <!-- Vues selon le rôle (commercial / manager / technicien / dispatcher) -->
    <div
      v-if="availableViews.length > 1"
      class="mb-3 flex flex-wrap items-center gap-1.5"
      role="group"
      aria-label="Vue des demandes"
    >
      <button
        v-for="v in availableViews"
        :key="v.value"
        type="button"
        class="rounded-full border px-3 py-1 text-sm font-medium transition-colors"
        :class="
          view === v.value
            ? 'border-secondary bg-secondary text-secondary-content'
            : 'border-base-300 bg-base-100 text-base-content/70 hover:border-secondary/50 hover:text-base-content'
        "
        :aria-pressed="view === v.value"
        @click="store.setView(v.value)"
      >
        {{ v.label }}
      </button>
    </div>

    <!-- Filtres par statut -->
    <div class="mb-4 flex flex-wrap items-center gap-1.5">
      <button
        v-for="s in ACCOMP_STATUS_ORDER"
        :key="s"
        type="button"
        class="rounded-full border px-3 py-1 text-sm font-medium transition-colors"
        :class="
          statusFilter.includes(s)
            ? 'border-primary bg-primary text-primary-content'
            : 'border-base-300 bg-base-100 text-base-content/70 hover:border-primary/50 hover:text-base-content'
        "
        :aria-pressed="statusFilter.includes(s)"
        @click="store.toggleStatus(s)"
      >
        {{ ACCOMP_STATUS_META[s].label }}
      </button>
    </div>

    <!-- Erreur -->
    <div v-if="error" role="alert" class="alert alert-error">
      <span>Une erreur est survenue lors du chargement des demandes.</span>
      <button type="button" class="btn btn-sm" @click="store.reload()">Réessayer</button>
    </div>

    <template v-else>
      <!-- Chargement initial -->
      <div
        v-if="loading && !rows.length"
        class="rounded-xl border border-base-200 bg-base-100 py-10 text-center shadow-sm"
      >
        <span class="loading loading-spinner" aria-hidden="true"></span>
        <p class="mt-2 text-base-content/70">Chargement des demandes…</p>
      </div>

      <!-- Aucune demande -->
      <div
        v-else-if="!rows.length"
        class="rounded-xl border border-base-200 bg-base-100 py-10 text-center text-base-content/70 shadow-sm"
      >
        Aucune demande ne correspond à ces critères.
      </div>

      <template v-else>
        <!-- Tableau (tablette / desktop ≥ md) -->
        <div class="hidden overflow-x-auto rounded-xl border border-base-200 bg-base-100 shadow-sm md:block">
          <table class="table">
            <thead>
              <tr>
                <th v-for="col in COLUMNS" :key="col.field" scope="col">
                  <button
                    type="button"
                    class="flex items-center gap-1 font-semibold hover:text-primary"
                    @click="store.setSort(col.field)"
                  >
                    {{ col.label }}
                    <ChevronUpIcon v-if="sort === col.field && order === 'asc'" class="h-3.5 w-3.5" aria-hidden="true" />
                    <ChevronDownIcon v-else-if="sort === col.field && order === 'desc'" class="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </th>
                <th scope="col">Client</th>
                <th scope="col">Type</th>
                <th scope="col">Demandeur</th>
                <th scope="col">Assigné à</th>
                <th scope="col">Délai attrib.</th>
                <th scope="col"><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="req in rows"
                :key="req.id"
                class="hover cursor-pointer"
                @click="goToDetail(req)"
              >
                <td class="font-medium tabular-nums">{{ req.reference }}</td>
                <td><AccompUrgencyBadge :urgency="req.urgency" /></td>
                <td>
                  <div class="flex items-center gap-1">
                    <AccompStatusBadge :status="req.status" />
                    <span
                      v-if="isTakeChargeOverdue(req)"
                      class="badge badge-error badge-sm gap-1"
                      title="Prise en charge en retard (> 48h ouvrées)"
                    >
                      <ClockIcon class="h-3 w-3" aria-hidden="true" />
                      Retard
                    </span>
                  </div>
                </td>
                <td class="tabular-nums">{{ formatDateShort(req.createdAt) }}</td>
                <td class="max-w-48 truncate font-medium">{{ req.client.name }}</td>
                <td>{{ ACCOMP_TYPE_LABEL[req.requestType] }}</td>
                <td class="max-w-40 truncate">{{ req.createdByName }}</td>
                <td>
                  <span v-if="req.assignedToName">{{ req.assignedToName }}</span>
                  <span v-else class="text-base-content/50">Non attribuée</span>
                </td>
                <td class="tabular-nums">{{ formatDuration(assignDelayMs(req)) }}</td>
                <td @click.stop>
                  <div class="flex justify-end gap-1">
                    <button
                      v-if="canSelfTake(req, perms) && !isDispatcher"
                      type="button"
                      class="btn btn-ghost btn-sm gap-1"
                      :disabled="actionLoading"
                      @click="selfTake(req)"
                    >
                      <HandRaisedIcon class="h-4 w-4" aria-hidden="true" />
                      Prendre
                    </button>
                    <button
                      v-if="canAssign(req, perms, currentUserUuid)"
                      type="button"
                      class="btn btn-ghost btn-sm gap-1"
                      @click="openAssign(req)"
                    >
                      <UserPlusIcon class="h-4 w-4" aria-hidden="true" />
                      {{ req.assignedToUuid ? 'Réaffecter' : 'Attribuer' }}
                    </button>
                    <button
                      v-if="isDispatcher && (req.status === 'NOUVELLE' || req.status === 'ATTRIBUEE')"
                      type="button"
                      class="btn btn-ghost btn-sm gap-1"
                      :disabled="actionLoading"
                      title="Me l'attribuer et démarrer"
                      @click="selfAssignAndStart(req)"
                    >
                      <PlayIcon class="h-4 w-4" aria-hidden="true" />
                      Me l'attribuer
                    </button>
                    <button
                      v-if="canTakeCharge(req, perms, currentUserUuid)"
                      type="button"
                      class="btn btn-ghost btn-sm gap-1"
                      :disabled="actionLoading"
                      @click="takeCharge(req)"
                    >
                      <BoltIcon class="h-4 w-4" aria-hidden="true" />
                      Prendre en charge
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Cartes (mobile < md) — tableau dense illisible sur petit écran -->
        <ul role="list" class="flex flex-col gap-3 md:hidden">
          <li
            v-for="req in rows"
            :key="req.id"
            class="rounded-xl border border-base-200 bg-base-100 p-4 shadow-sm"
          >
            <div class="cursor-pointer" @click="goToDetail(req)">
              <div class="flex items-start justify-between gap-2">
                <span class="font-medium tabular-nums text-base-content">{{ req.reference }}</span>
                <div class="flex shrink-0 flex-wrap items-center gap-1.5">
                  <AccompUrgencyBadge :urgency="req.urgency" />
                  <AccompStatusBadge :status="req.status" />
                  <span v-if="isTakeChargeOverdue(req)" class="badge badge-error badge-sm gap-1">
                    <ClockIcon class="h-3 w-3" aria-hidden="true" />
                    Retard
                  </span>
                </div>
              </div>
              <p class="mt-1 font-semibold text-base-content">{{ req.client.name }}</p>
              <dl class="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
                <div class="flex flex-col">
                  <dt class="text-xs uppercase tracking-wide text-base-content/70">Type</dt>
                  <dd class="text-base-content">{{ ACCOMP_TYPE_LABEL[req.requestType] }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs uppercase tracking-wide text-base-content/70">Créée le</dt>
                  <dd class="tabular-nums text-base-content">{{ formatDateShort(req.createdAt) }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs uppercase tracking-wide text-base-content/70">Demandeur</dt>
                  <dd class="text-base-content">{{ req.createdByName }}</dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs uppercase tracking-wide text-base-content/70">Assigné à</dt>
                  <dd class="text-base-content">
                    <span v-if="req.assignedToName">{{ req.assignedToName }}</span>
                    <span v-else class="text-base-content/50">Non attribuée</span>
                  </dd>
                </div>
                <div class="flex flex-col">
                  <dt class="text-xs uppercase tracking-wide text-base-content/70">Délai attrib.</dt>
                  <dd class="tabular-nums text-base-content">{{ formatDuration(assignDelayMs(req)) }}</dd>
                </div>
              </dl>
            </div>
            <div
              v-if="(canSelfTake(req, perms) && !isDispatcher) || canAssign(req, perms, currentUserUuid) || (isDispatcher && (req.status === 'NOUVELLE' || req.status === 'ATTRIBUEE')) || canTakeCharge(req, perms, currentUserUuid)"
              class="mt-2 flex flex-wrap gap-1 border-t border-base-200 pt-2"
            >
              <button
                v-if="canSelfTake(req, perms) && !isDispatcher"
                type="button"
                class="btn btn-ghost btn-sm gap-1"
                :disabled="actionLoading"
                @click="selfTake(req)"
              >
                <HandRaisedIcon class="h-4 w-4" aria-hidden="true" />
                Prendre
              </button>
              <button
                v-if="canAssign(req, perms, currentUserUuid)"
                type="button"
                class="btn btn-ghost btn-sm gap-1"
                @click="openAssign(req)"
              >
                <UserPlusIcon class="h-4 w-4" aria-hidden="true" />
                {{ req.assignedToUuid ? 'Réaffecter' : 'Attribuer' }}
              </button>
              <button
                v-if="isDispatcher && (req.status === 'NOUVELLE' || req.status === 'ATTRIBUEE')"
                type="button"
                class="btn btn-ghost btn-sm gap-1"
                :disabled="actionLoading"
                title="Me l'attribuer et démarrer"
                @click="selfAssignAndStart(req)"
              >
                <PlayIcon class="h-4 w-4" aria-hidden="true" />
                Me l'attribuer
              </button>
              <button
                v-if="canTakeCharge(req, perms, currentUserUuid)"
                type="button"
                class="btn btn-ghost btn-sm gap-1"
                :disabled="actionLoading"
                @click="takeCharge(req)"
              >
                <BoltIcon class="h-4 w-4" aria-hidden="true" />
                Prendre en charge
              </button>
            </div>
          </li>
        </ul>
      </template>
    </template>

    <!-- Pagination -->
    <div v-if="!error" class="mt-4 flex flex-wrap items-center justify-between gap-3">
      <span class="text-sm text-base-content/70">
        <template v-if="total > 0">{{ from }}–{{ to }} sur {{ total }} demande{{ total > 1 ? 's' : '' }}</template>
        <template v-else>0 demande</template>
      </span>
      <div class="join">
        <button
          type="button"
          class="btn btn-sm join-item"
          :disabled="page <= 1 || loading"
          aria-label="Page précédente"
          @click="store.setPage(page - 1)"
        >
          <ChevronLeftIcon class="h-4 w-4" aria-hidden="true" />
        </button>
        <span class="btn btn-sm join-item pointer-events-none">Page {{ page }} / {{ pageCount }}</span>
        <button
          type="button"
          class="btn btn-sm join-item"
          :disabled="page >= pageCount || loading"
          aria-label="Page suivante"
          @click="store.setPage(page + 1)"
        >
          <ChevronRightIcon class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>

    <AccompAssignDialog
      :request="assignTarget"
      :loading="actionLoading"
      @close="assignTarget = null"
      @confirmed="onAssignConfirmed"
    />
  </div>
</template>
